import { Icon } from "./Icon";
import { useEffect, useRef, useState } from "react";
import { supabase } from "../lib/supabase";
import { voiceClock } from "../lib/messages";
import { AUDIO_BITS, isMissingObject, VIDEO_BITS, videoRecorderMime, videoShape } from "../lib/reviewMedia";

/* W82 STEP 5 — THE ROUND VIDEO AND THE LIVE REVIEW (his §6 items 1–3, §8, §11): ONE video recorder, ONE video player.
 *
 * RECORD — `getUserMedia` (the front camera + the mic) is drawn onto a CANVAS and the canvas's own stream is recorded, so
 * what is recorded is exactly what is drawn: a square centre-crop for a round comment (played ROUND by the players — the
 * phone's too), or, for the LIVE REVIEW, the TAB (`getDisplayMedia`) with the face in a circle at the bottom-right. The
 * container is MP4/H.264 or nothing (`videoRecorderMime`): Firefox gets no record button and the sentence naming it.
 * Each recording is its own SESSION (the voice hook's lesson): the review stops one and starts the next in the same tick.
 * ⚠️ THE LIVE REVIEW STOPS ON NAVIGATION — the page unmounting (a route change) ends every session and drops the take —
 * and when the browser's own "Stop sharing" ends the screen track, the take is KEPT (the coach chose to stop).
 * ⚠️ NO <video> PLAYS WITH SOUND BY ITSELF: the live preview bubble is `muted` (it is the coach's own camera), and every
 * player starts paused until a click (the gate `w82-5-the-media` asserts both).
 */

export type VideoTake = { blob: Blob; seconds: number; tag: string; live: boolean };

type Session = {
  rec: MediaRecorder; streams: MediaStream[]; chunks: Blob[]; startedAt: number; tag: string; live: boolean; ceiling: number;
  raf: number | null; timer: number | null; tick: number | null; els: HTMLVideoElement[];
};

export type VideoRecording = {
  mime: string | null;
  recording: boolean;
  live: boolean;
  elapsed: number;
  error: string | null;
  /** The camera, for the floating round preview while recording (muted — it is the coach's own face). */
  preview: MediaStream | null;
  start: (opts: { tag: string; ceilingSeconds: number; live: boolean }) => Promise<boolean>;
  release: () => void;
};

function playable(stream: MediaStream): HTMLVideoElement {
  const el = document.createElement("video");
  el.muted = true;
  el.playsInline = true;
  el.srcObject = stream;
  void el.play().catch(() => undefined);
  return el;
}

/** Draw `src` centre-cropped into the square (x, y, size). */
function drawSquare(ctx: CanvasRenderingContext2D, src: HTMLVideoElement, x: number, y: number, size: number) {
  const w = src.videoWidth, h = src.videoHeight;
  if (!(w > 0 && h > 0)) return;
  const side = Math.min(w, h);
  // mirrored, as a front camera is seen in a mirror (and as the phone's front camera records it)
  ctx.save();
  ctx.translate(x + size, y);
  ctx.scale(-1, 1);
  ctx.drawImage(src, (w - side) / 2, (h - side) / 2, side, side, 0, 0, size, size);
  ctx.restore();
}

export function useVideoRecording({ onTake }: { onTake: (take: VideoTake) => void }): VideoRecording {
  const [recording, setRecording] = useState(false);
  const [live, setLive] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<MediaStream | null>(null);
  const current = useRef<Session | null>(null);
  const pending = useRef(false);
  const takeTo = useRef(onTake);
  takeTo.current = onTake;

  const mime = typeof MediaRecorder === "undefined" ? null
    : videoRecorderMime((m) => { try { return MediaRecorder.isTypeSupported(m); } catch { return false; } });

  // THE PAGE GOES (a route change): every session ends and its take is DROPPED — the review it belonged to is gone.
  useEffect(() => () => {
    pending.current = false;
    const s = current.current;
    current.current = null;
    if (s !== null) { s.rec.onstop = null; if (s.rec.state !== "inactive") s.rec.stop(); teardown(s); }
  }, []);

  function teardown(s: Session) {
    if (s.raf !== null) cancelAnimationFrame(s.raf);
    if (s.timer !== null) window.clearTimeout(s.timer);
    if (s.tick !== null) window.clearInterval(s.tick);
    s.raf = s.timer = s.tick = null;
    for (const st of s.streams) st.getTracks().forEach((tr) => tr.stop());
    for (const el of s.els) el.srcObject = null;
  }

  async function start(opts: { tag: string; ceilingSeconds: number; live: boolean }): Promise<boolean> {
    if (mime === null || current.current !== null || pending.current) return false;
    pending.current = true;
    setError(null);
    const streams: MediaStream[] = [];
    let face: MediaStream;
    let screen: MediaStream | null = null;
    try {
      face = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 640 } }, audio: true });
      streams.push(face);
      if (opts.live) {
        if (typeof navigator.mediaDevices.getDisplayMedia !== "function") throw new Error("display");
        // `preferCurrentTab`: the live review is of THIS tab — the coach's own review page.
        screen = await navigator.mediaDevices.getDisplayMedia({ video: { frameRate: 30 }, audio: false, preferCurrentTab: true } as DisplayMediaStreamOptions);
        streams.push(screen);
      }
    } catch (e) {
      for (const st of streams) st.getTracks().forEach((tr) => tr.stop());
      pending.current = false;
      setError(e instanceof Error && e.message === "display"
        ? "This browser cannot record the screen. Chrome or Safari on a computer can."
        : opts.live ? "The camera or the screen was not allowed. Your browser's site settings can allow them."
        : "The camera was not allowed. Your browser's site settings can allow it.");
      return false;
    }
    if (!pending.current) { for (const st of streams) st.getTracks().forEach((tr) => tr.stop()); return false; }
    pending.current = false;

    const canvas = document.createElement("canvas");
    canvas.width = opts.live ? 1280 : 480;
    canvas.height = opts.live ? 720 : 480;
    const ctx = canvas.getContext("2d");
    if (ctx === null) { for (const st of streams) st.getTracks().forEach((tr) => tr.stop()); setError("This browser cannot draw the video."); return false; }
    const faceEl = playable(face);
    const screenEl = screen === null ? null : playable(screen);
    const out = canvas.captureStream(30);
    face.getAudioTracks().forEach((tr) => out.addTrack(tr));
    const rec = new MediaRecorder(out, { mimeType: mime, videoBitsPerSecond: opts.live ? VIDEO_BITS.live : VIDEO_BITS.round, audioBitsPerSecond: AUDIO_BITS });
    const s: Session = {
      rec, streams: [...streams, out], chunks: [], startedAt: performance.now(), tag: opts.tag, live: opts.live, ceiling: opts.ceilingSeconds,
      raf: null, timer: null, tick: null, els: screenEl === null ? [faceEl] : [faceEl, screenEl],
    };
    const draw = () => {
      if (current.current !== s) return;
      if (screenEl !== null) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        const w = screenEl.videoWidth, h = screenEl.videoHeight;
        if (w > 0 && h > 0) {
          const k = Math.min(canvas.width / w, canvas.height / h);
          ctx.drawImage(screenEl, (canvas.width - w * k) / 2, (canvas.height - h * k) / 2, w * k, h * k);
        }
        // the face, round, bottom-right
        const d = 220, x = canvas.width - d - 28, y = canvas.height - d - 28;
        ctx.save();
        ctx.beginPath();
        ctx.arc(x + d / 2, y + d / 2, d / 2, 0, Math.PI * 2);
        ctx.clip();
        drawSquare(ctx, faceEl, x, y, d);
        ctx.restore();
      } else {
        drawSquare(ctx, faceEl, 0, 0, canvas.width);
      }
      s.raf = requestAnimationFrame(draw);
    };
    rec.ondataavailable = (e) => { if (e.data.size > 0) s.chunks.push(e.data); };
    rec.onstop = () => {
      const seconds = Math.min(s.ceiling, Math.max(1, Math.round((performance.now() - s.startedAt) / 1000)));
      teardown(s);
      if (current.current === s) current.current = null;
      if (current.current === null) { setRecording(false); setPreview(null); }
      const blob = new Blob(s.chunks, { type: "video/mp4" });
      if (blob.size > 0) takeTo.current({ blob, seconds, tag: s.tag, live: s.live });
    };
    // the browser's own "Stop sharing" ends the screen track: that is the coach stopping — the take is kept
    screen?.getVideoTracks().forEach((tr) => tr.addEventListener("ended", () => { if (current.current === s) release(); }));
    current.current = s;
    rec.start(1000);
    s.raf = requestAnimationFrame(draw);
    s.tick = window.setInterval(() => { if (current.current === s) setElapsed((performance.now() - s.startedAt) / 1000); }, 250);
    s.timer = window.setTimeout(() => { if (current.current === s) release(); }, opts.ceilingSeconds * 1000);
    setRecording(true);
    setLive(opts.live);
    setElapsed(0);
    setPreview(face);
    return true;
  }

  function release() {
    pending.current = false;
    const s = current.current;
    current.current = null;
    if (s !== null && s.rec.state !== "inactive") s.rec.stop();
  }

  return { mime, recording, live, elapsed, error, preview, start, release };
}

/** While a video records: the coach's face in a round bubble, bottom-right, a red dot and the clock. MUTED — it is his own
 *  camera; nothing here plays sound. */
export function RecordingBubble({ stream, live, elapsed, ceiling }: { stream: MediaStream | null; live: boolean; elapsed: number; ceiling: number }) {
  const el = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    if (el.current !== null) el.current.srcObject = stream;
  }, [stream]);
  if (stream === null) return null;
  return (
    <div className="rv-bubble" role="status" aria-live="polite">
      <video ref={el} className="rv-bubble-face" muted autoPlay playsInline aria-hidden="true" />
      <span className="rv-bubble-clock"><span className="rv-dot" aria-hidden="true" />{live ? "Live review · " : ""}{voiceClock(elapsed)} / {voiceClock(ceiling)}</span>
    </div>
  );
}

/**
 * 🔴 THE ONE VIDEO PLAYER: a round self-video, or the live review's wide one (read from the file itself — `videoShape` — so
 * no column says which). Starts PAUSED on its first frame; a click plays WITH sound; never `autoPlay`, never `controls`
 * (the page's own play button, as the voice player's).
 */
export function RoundVideoPlayer({ src, seconds, unavailable }: { src: string | null; seconds: number; unavailable: string | null }) {
  const el = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);
  const [position, setPosition] = useState(0);
  const [shape, setShape] = useState<"round" | "wide">("round");
  function toggle() {
    const v = el.current;
    if (v === null || src === null) return;
    if (v.paused) void v.play(); else v.pause();
  }
  return (
    <div className={`rv-player is-${shape}`}>
      <button type="button" className="rv-stage" onClick={toggle} disabled={src === null}
              aria-label={playing ? "Pause video" : "Play video"}>
        {src !== null && (
          <video ref={el} className="rv-video" src={src} playsInline preload="metadata"
                 onLoadedMetadata={(e) => setShape(videoShape(e.currentTarget.videoWidth, e.currentTarget.videoHeight))}
                 onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => setPlaying(false)}
                 onTimeUpdate={(e) => setPosition(e.currentTarget.currentTime)} />
        )}
        {!playing && (
          <span className="rv-play" aria-hidden="true">
            <Icon name="play" />
          </span>
        )}
      </button>
      <span className="rv-time">{playing ? voiceClock(position) : voiceClock(seconds)}</span>
      {unavailable !== null && <span className="rv-note">{unavailable}</span>}
    </div>
  );
}

/** A signed URL for one private object, minted for THIS reader (only the two parties may read); `missing` when the object
 *  is gone (its owner removed it — the row keeps its path). */
export function useSignedUrl(bucket: string, path: string | null, gen = 0): { src: string | null; missing: boolean; failed: boolean } {
  const [state, setState] = useState<{ src: string | null; missing: boolean; failed: boolean }>({ src: null, missing: false, failed: false });
  useEffect(() => {
    if (path === null) return;
    let cancelled = false;
    setState({ src: null, missing: false, failed: false });
    void (async () => {
      const signed = await supabase.storage.from(bucket).createSignedUrl(path, 3600);
      if (cancelled) return;
      if (signed.error !== null) { setState({ src: null, missing: isMissingObject(signed.error), failed: !isMissingObject(signed.error) }); return; }
      setState({ src: signed.data.signedUrl, missing: false, failed: false });
    })();
    return () => { cancelled = true; };
  }, [bucket, path, gen]);
  return state;
}

/** A SENT video: the signed URL (`useSignedUrl`); a removed object reads `removedWords`, any other failure that it could
 *  not be fetched. */
export function SentVideo({ bucket, path, seconds, removedWords, gen = 0 }: { bucket: string; path: string; seconds: number; removedWords: string; gen?: number }) {
  const s = useSignedUrl(bucket, path, gen);
  return <RoundVideoPlayer src={s.src} seconds={seconds} unavailable={s.missing ? removedWords : s.failed ? "This video could not be fetched." : null} />;
}
